import React, { useState } from "react";
import {
  AreaChart, Area,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";
import placeholderImg from "../images/design.png";

// ─── Theme (matches admin panel) ─────────────────────────────────────────────
const T = {
  bg: "#0b0f1a", sidebar: "#0f172a", card: "#0c1220", cardAlt: "#111827",
  accent: "#2185ff", accentDim: "rgba(13,102,211,0.15)", accentBorder: "rgba(28,139,250,0.35)",
  border: "rgba(255,255,255,0.06)", borderMid: "rgba(255,255,255,0.10)",
  text: "#ffffff", muted: "#b4b6b9", faint: "#b4b6b9",
  orange: "#f97316", red: "#ef4444", blue: "#60a5fa", yellow: "#fbbf24",
};

// ─── Mock User Data ───────────────────────────────────────────────────────────
const USER = {
  name: "Marcus Rivera",
  email: "marcus.rivera@email.com",
  memberSince: "March 2022",
  avatar: "MR",
  location: "Chicago, Illinois, US",
  phone: "+1 (312) 555-0194",
};

const USER_STATS = [
  { label: "Total Orders",    value: "24",      pct: "+3 this month", up: true  },
  { label: "Total Spent",     value: "$3,482",  pct: "+$420 vs last", up: true  },
  { label: "Active Orders",   value: "2",       pct: "In Progress",   up: true  },
  //{ label: "Loyalty Points",  value: "1,840",   pct: "+120 pts",      up: true  },
];

const SPEND_DATA = [
  { m: "Jan", spent: 210 }, { m: "Feb", spent: 340 }, { m: "Mar", spent: 180 },
  { m: "Apr", spent: 520 }, { m: "May", spent: 390 }, { m: "Jun", spent: 620 },
  { m: "Jul", spent: 280 }, { m: "Aug", spent: 740 }, { m: "Sep", spent: 410 },
  { m: "Oct", spent: 550 }, { m: "Nov", spent: 480 }, { m: "Dec", spent: 760 },
];

const ORDERS_DATA = [
  { m: "Jan", orders: 1 }, { m: "Feb", orders: 2 }, { m: "Mar", orders: 1 },
  { m: "Apr", orders: 3 }, { m: "May", orders: 2 }, { m: "Jun", orders: 4 },
  { m: "Jul", orders: 1 }, { m: "Aug", orders: 3 }, { m: "Sep", orders: 2 },
  { m: "Oct", orders: 3 }, { m: "Nov", orders: 2 }, { m: "Dec", orders: 4 },
];

const MY_ORDERS = [
  {
    id: "#53200014", product: "Soccer Kit — Diagonal A", qty: 2,
    date: "Mar 22, 2025", total: "$253.82", status: "Completed",
    tracking: "Delivered",
  },
  {
    id: "#53200021", product: "Training Jersey", qty: 1,
    date: "Feb 10, 2025", total: "$89.50", status: "Shipping",
    tracking: "In Transit · ETA Apr 10",
  },
  {
    id: "#53200028", product: "Goalkeeper Kit", qty: 3,
    date: "Jan 5, 2025", total: "$412.00", status: "Pending",
    tracking: "Processing",
  },
  {
    id: "#53200033", product: "Soccer Kit — Stripe B", qty: 1,
    date: "Dec 18, 2024", total: "$134.99", status: "Completed",
    tracking: "Delivered",
  },
  {
    id: "#53200040", product: "Club Polo", qty: 2,
    date: "Nov 30, 2024", total: "$76.00", status: "Refund",
    tracking: "Refunded",
  },
  {
    id: "#53200047", product: "Youth Kit Set", qty: 4,
    date: "Oct 12, 2024", total: "$620.00", status: "Completed",
    tracking: "Delivered",
  },
];

const NOTIFICATIONS = [
  { icon: "🚚", text: "Your order #53200021 is on the way!",     time: "2 hrs ago",  highlight: true  },
  { icon: "✅", text: "Order #53200014 was delivered.",           time: "Mar 22",     highlight: false },
  { icon: "🎁", text: "You earned 120 loyalty points.",           time: "Mar 22",     highlight: false },
  { icon: "🔔", text: "New kit designs available — Spring 2025.", time: "Mar 18",     highlight: false },
  { icon: "💬", text: "Support replied to your inquiry.",         time: "Mar 15",     highlight: false },
];

const MY_KIT_CONFIG = {
  category: "Soccer Kits",
  neckStyle: "Crew Neck",
  sleeveType: "Half Sleeves",
  design: "Diagonal A",
  colors: {
    shirtFront: "#ffd200", shirtBack: "#ffd200",
    leftArmStripe: "#2a2828", rightArmStripe: "#2a2828",
    neckCollar: "#cfab0f", pants: "#2a2828",
  },
  pattern: "Zig Zag (Front)",
  gradient: "Off",
  numbers: "Front: 10 / Back: 10",
  backName: "RIVERA",
  logos: "Swoosh (Shirt), Brand X (Shorts)",
};

const SAVED_DESIGNS = [
  { name: "Main Kit",    desc: "Diagonal A · Yellow/Black", active: true  },
  { name: "Away Kit",    desc: "Stripe B · White/Blue",     active: false },
  { name: "Training",    desc: "Plain · Navy",               active: false },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function statusColor(s) {
  if (s === "Completed") return T.accent;
  if (s === "Shipping")  return T.blue;
  if (s === "Pending")   return T.orange;
  if (s === "Refund")    return T.red;
  return T.muted;
}

function statusBg(s) {
  if (s === "Completed") return "rgba(34,197,94,0.12)";
  if (s === "Shipping")  return "rgba(96,165,250,0.12)";
  if (s === "Pending")   return "rgba(249,115,22,0.12)";
  if (s === "Refund")    return "rgba(239,68,68,0.12)";
  return "rgba(255,255,255,0.06)";
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: T.cardAlt, border: `1px solid ${T.accentBorder}`,
      borderRadius: 8, padding: "8px 14px", fontSize: 12, color: T.text,
    }}>
      <p style={{ margin: 0, color: T.muted }}>{label}</p>
      <p style={{ margin: "4px 0 0", color: T.accent, fontWeight: 600 }}>
        {payload[0]?.name === "spent" ? `$${payload[0].value}` : payload[0]?.value}
      </p>
    </div>
  );
};

// ─── NAV ─────────────────────────────────────────────────────────────────────
const NAV = [
  { key: "overview",  label: "Overview",    icon: "⊞" },
  { key: "orders",    label: "My Orders",   icon: "📦" },
  { key: "mykit",     label: "My Kit",      icon: "👕" },
  { key: "designs",   label: "Saved Designs", icon: "🎨" },
  { key: "profile",   label: "Profile",     icon: "👤" },
];

// ─── STAT CARD ────────────────────────────────────────────────────────────────
function StatCard({ label, value, pct, up }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`, borderRadius: 14,
      padding: "18px 22px", flex: 1, minWidth: 160,
    }}>
      <p style={{ margin: 0, fontSize: 12, color: T.muted, marginBottom: 6 }}>{label}</p>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 24, fontWeight: 700, color: T.text }}>{value}</span>
        <span style={{
          fontSize: 11, fontWeight: 600,
          color: up ? T.accent : T.red,
          marginBottom: 3, whiteSpace: "nowrap",
        }}>{pct}</span>
      </div>
    </div>
  );
}

// ─── PAGE: OVERVIEW ───────────────────────────────────────────────────────────
function OverviewPage() {
  return (
    <div>
      {/* Welcome banner */}
      <div style={{
        background: `linear-gradient(135deg, rgba(13,102,211,0.15) 0%, rgba(35, 112, 207, 0.25) 100%)`,
        border: `1px solid ${T.accentBorder}`, borderRadius: 16,
        padding: "24px 28px", marginBottom: 24,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <p style={{ margin: "0 0 4px", fontSize: 13, color: T.accent, fontWeight: 600 }}>
            Welcome back 👋
          </p>
          <h2 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 700, color: T.text }}>
            {USER.name}
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: T.muted }}>
           Member since {USER.memberSince}
          </p>
        </div>
        {/*
        <div style={{ textAlign: "right" }}>
          <div style={{
            background: `linear-gradient(135deg, #f59e0b, #f97316)`,
            borderRadius: 10, padding: "6px 16px", display: "inline-block",
            fontSize: 12, fontWeight: 700, color: "#fff", marginBottom: 6,
          }}>
            ⭐ Gold Member
          </div>
          <p style={{ margin: 0, fontSize: 12, color: T.muted }}>1,840 loyalty pts</p>
        </div>8*/}
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
        {USER_STATS.map(s => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Charts row */}
      {/*
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
        
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Spend History</span>
            <span style={{ fontSize: 12, color: T.muted }}>Last 12 months</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={SPEND_DATA}>
              <defs>
                <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={T.accent} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={T.accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={T.border} strokeDasharray="3 3" />
              <XAxis dataKey="m" tick={{ fill: T.faint, fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: T.faint, fontSize: 10 }} axisLine={false} tickLine={false}
                tickFormatter={v => `$${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="spent" stroke={T.accent} strokeWidth={2.5}
                fill="url(#spendGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

       
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Orders per Month</span>
            <span style={{ fontSize: 12, color: T.muted }}>Last 12 months</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={ORDERS_DATA}>
              <CartesianGrid stroke={T.border} strokeDasharray="3 3" />
              <XAxis dataKey="m" tick={{ fill: T.faint, fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: T.faint, fontSize: 10 }} axisLine={false} tickLine={false}
                allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="orders" stroke={T.blue} strokeWidth={2.5} dot={false}
                activeDot={{ r: 5, fill: T.blue, stroke: T.card, strokeWidth: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>*/}

      {/* Bottom row: recent orders + notifications */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 14 }}>
        {/* Recent orders */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Recent Orders</span>
            <span style={{ fontSize: 12, color: T.accent, cursor: "pointer" }}>View All →</span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {["Order", "Product", "Date", "Total", "Status"].map(h => (
                  <th key={h} style={{
                    textAlign: "left", color: T.faint, fontWeight: 500,
                    paddingBottom: 10, borderBottom: `1px solid ${T.border}`,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MY_ORDERS.slice(0, 4).map((r, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={{ padding: "12px 0", color: T.muted, fontSize: 12 }}>{r.id}</td>
                  <td style={{ color: T.text, fontWeight: 500, paddingRight: 12 }}>{r.product}</td>
                  <td style={{ color: T.muted, whiteSpace: "nowrap" }}>{r.date}</td>
                  <td style={{ color: T.text, fontWeight: 600 }}>{r.total}</td>
                  <td>
                    <span style={{
                      color: statusColor(r.status), background: statusBg(r.status),
                      borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600,
                    }}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Notifications */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Notifications</span>
            <span style={{
              background: T.red, color: "#fff", borderRadius: "50%",
              width: 18, height: 18, display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: 11, fontWeight: 700,
            }}>1</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {NOTIFICATIONS.map((n, i) => (
              <div key={i} style={{
                display: "flex", gap: 12, padding: "10px 12px", borderRadius: 10,
                background: n.highlight ? T.accentDim : "transparent",
                border: n.highlight ? `1px solid ${T.accentBorder}` : "1px solid transparent",
                marginBottom: 2,
              }}>
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{n.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: "0 0 2px", fontSize: 12, color: T.text,
                    lineHeight: 1.4, fontWeight: n.highlight ? 600 : 400,
                  }}>{n.text}</p>
                  <p style={{ margin: 0, fontSize: 11, color: T.faint }}>{n.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PAGE: MY ORDERS ─────────────────────────────────────────────────────────
function MyOrdersPage() {
  const [filter, setFilter] = useState("All");
  const statuses = ["All", "Completed", "Shipping", "Pending", "Refund"];
  const filtered = filter === "All" ? MY_ORDERS : MY_ORDERS.filter(o => o.status === filter);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: T.text }}>My Orders</h1>
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 16px", fontSize: 13, color: T.muted }}>
          All Time ▾
        </div>
      </div>

      {/* Status filter pills */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {statuses.map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{
            background: filter === s ? T.accentDim : T.card,
            border: filter === s ? `1px solid ${T.accentBorder}` : `1px solid ${T.border}`,
            color: filter === s ? T.accent : T.muted,
            borderRadius: 20, padding: "6px 16px", cursor: "pointer",
            fontSize: 13, fontWeight: filter === s ? 600 : 400,
            transition: "all 0.15s",
          }}>{s}</button>
        ))}
      </div>

      {/* Orders list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {filtered.map((order, i) => (
          <div key={i} style={{
            background: T.card, border: `1px solid ${T.border}`, borderRadius: 14,
            padding: "18px 22px",
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{order.product}</span>
                  <span style={{
                    color: statusColor(order.status), background: statusBg(order.status),
                    borderRadius: 6, padding: "2px 10px", fontSize: 12, fontWeight: 600,
                  }}>{order.status}</span>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: T.muted }}>{order.id} · Qty: {order.qty} · {order.date}</p>
              </div>
              <span style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{order.total}</span>
            </div>

            {/* Tracking bar */}
            <div style={{
              background: T.cardAlt, borderRadius: 8, padding: "10px 14px",
              display: "flex", alignItems: "center", gap: 10,
              border: `1px solid ${T.border}`,
            }}>
              <span style={{ fontSize: 14 }}>
                {order.status === "Completed" ? "✅" :
                 order.status === "Shipping"  ? "🚚" :
                 order.status === "Pending"   ? "⏳" : "↩️"}
              </span>
              <span style={{ fontSize: 13, color: T.muted }}>{order.tracking}</span>
              {order.status === "Shipping" && (
                <span style={{
                  marginLeft: "auto", fontSize: 12, color: T.accent,
                  cursor: "pointer", fontWeight: 600,
                }}>Track →</span>
              )}
              {order.status === "Completed" && (
                <span style={{
                  marginLeft: "auto", fontSize: 12, color: T.blue,
                  cursor: "pointer", fontWeight: 600,
                }}>Reorder →</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PAGE: MY KIT (read-only config viewer) ───────────────────────────────────
function MyKitPage() {
  const configRows = [
    { label: "Category",   value: MY_KIT_CONFIG.category   },
    { label: "Neck Style", value: MY_KIT_CONFIG.neckStyle  },
    { label: "Sleeves",    value: MY_KIT_CONFIG.sleeveType },
    { label: "Design",     value: MY_KIT_CONFIG.design     },
    { label: "Pattern",    value: MY_KIT_CONFIG.pattern    },
    { label: "Gradient",   value: MY_KIT_CONFIG.gradient   },
    { label: "Numbers",    value: MY_KIT_CONFIG.numbers    },
    { label: "Back Name",  value: MY_KIT_CONFIG.backName   },
    { label: "Logos",      value: MY_KIT_CONFIG.logos      },
  ];
  const colorEntries = Object.entries(MY_KIT_CONFIG.colors);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: T.text }}>My Kit</h1>
        <button style={{
          background: "linear-gradient(135deg, #2185ff, #1d66cc)",
          border: "none", color: "#fff", borderRadius: 10,
          padding: "10px 22px", cursor: "pointer",
          fontSize: 13, fontWeight: 700,
          boxShadow: "0 4px 14px rgba(33, 133, 255, 0.35)",
        }}>✏️ Edit Configuration</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
        {/* Product image */}
        <div style={{
          background: T.card, border: `1px solid ${T.border}`, borderRadius: 14,
          padding: 24, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320,
        }}>
          <img
            src={placeholderImg}
            alt="My Kit"
            style={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain", borderRadius: 8 }}
          />
        </div>

        {/* Configuration table */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 24 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: T.text }}>
            Current Configuration
          </h3>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <tbody>
              {configRows.map(row => (
                <tr key={row.label} style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={{ padding: "10px 0", fontWeight: 600, color: T.text, width: 100, verticalAlign: "top" }}>
                    {row.label}
                  </td>
                  <td style={{ padding: "10px 0 10px 12px", color: T.muted }}>
                    {row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Color swatches */}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 24 }}>
        <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 700, color: T.text }}>
          Colors
        </h3>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {colorEntries.map(([key, hex]) => (
            <div key={key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 10,
                background: hex,
                border: `2px solid ${T.border}`,
                boxShadow: `0 2px 8px ${hex}55`,
              }} />
              <span style={{ fontSize: 11, color: T.muted, textAlign: "center", maxWidth: 70, lineHeight: 1.3 }}>
                {key.replace(/([A-Z])/g, " $1").trim()}
              </span>
              <span style={{ fontSize: 10, color: T.faint, fontFamily: "monospace" }}>{hex}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── PAGE: SAVED DESIGNS ──────────────────────────────────────────────────────
function SavedDesignsPage() {
  const [active, setActive] = useState(0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: T.text }}>Saved Designs</h1>
        <button style={{
          background: T.accentDim, border: `1px solid ${T.accentBorder}`,
          color: T.accent, borderRadius: 10, padding: "8px 20px",
          cursor: "pointer", fontSize: 13, fontWeight: 600,
        }}>+ New Design</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
        {SAVED_DESIGNS.map((d, i) => (
          <div key={i}
            onClick={() => setActive(i)}
            style={{
              background: T.card,
              border: active === i ? `1px solid ${T.accentBorder}` : `1px solid ${T.border}`,
              borderRadius: 14, padding: 20, cursor: "pointer",
              transition: "all 0.15s",
              outline: active === i ? `1px solid ${T.accentBorder}` : "none",
            }}>
            {/* Preview placeholder */}
            <div style={{
              background: T.cardAlt, borderRadius: 10, height: 160,
              display: "flex", alignItems: "center", justifyContent: "center",
              marginBottom: 16, border: `1px solid ${T.border}`,
              fontSize: 48,
            }}>👕</div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: T.text }}>
                  {d.name}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: T.muted }}>{d.desc}</p>
              </div>
              {i === active && (
                <span style={{
                  background: T.accentDim, border: `1px solid ${T.accentBorder}`,
                  color: T.accent, borderRadius: 6, padding: "2px 8px",
                  fontSize: 11, fontWeight: 600,
                }}>Active</span>
              )}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button style={{
                flex: 1, background: "transparent", border: `1px solid ${T.border}`,
                color: T.muted, borderRadius: 8, padding: "6px 0",
                cursor: "pointer", fontSize: 12,
              }}>✏️ Edit</button>
              <button style={{
                flex: 1,
                background: i === active ? T.accentDim : "transparent",
                border: i === active ? `1px solid ${T.accentBorder}` : `1px solid ${T.border}`,
                color: i === active ? T.accent : T.muted,
                borderRadius: 8, padding: "6px 0",
                cursor: "pointer", fontSize: 12, fontWeight: i === active ? 600 : 400,
              }}>
                {i === active ? "✅ Selected" : "Select"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PAGE: PROFILE ────────────────────────────────────────────────────────────
function ProfilePage() {
  const fields = [
    { label: "Full Name",     value: USER.name,        type: "text"  },
    { label: "Email",         value: USER.email,       type: "email" },
    { label: "Phone",         value: USER.phone,       type: "tel"   },
    { label: "Location",      value: USER.location,    type: "text"  },
    { label: "Member Since",  value: USER.memberSince, type: "text", readOnly: true },
   // { label: "Membership",    value: USER.tier,        type: "text", readOnly: true },
  ];

  return (
    <div>
      <h1 style={{ margin: "0 0 24px", fontSize: 22, fontWeight: 700, color: T.text }}>Profile</h1>

      <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 14 }}>
        {/* Avatar card */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{
              width: 80, height: 80, borderRadius: "50%",
              background: "#0d66d349", border: `2px solid ${T.accentBorder}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 28, fontWeight: 700, color: T.accent, marginBottom: 14,
            }}>{USER.avatar}</div>
            <p style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700, color: T.text }}>{USER.name}</p>
            <p style={{ margin: "0 0 12px", fontSize: 12, color: T.muted }}>{USER.email}</p>
            
          </div>

          <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 20, paddingTop: 20 }}>
            {[
              { label: "Total Orders", val: "24" },
              { label: "Total Spent",  val: "$3,482" },
              
            ].map(r => (
              <div key={r.label} style={{
                display: "flex", justifyContent: "space-between",
                marginBottom: 12, fontSize: 13,
              }}>
                <span style={{ color: T.muted }}>{r.label}</span>
                <span style={{ color: T.text, fontWeight: 600 }}>{r.val}</span>
              </div>
            ))}
          </div>

          <button style={{
            width: "100%", marginTop: 8,
            background: T.accentDim, border: `1px solid ${T.accentBorder}`,
            color: T.accent, borderRadius: 10, padding: "10px 0",
            cursor: "pointer", fontSize: 13, fontWeight: 600,
          }}>Change Photo</button>
        </div>

        {/* Edit form */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28 }}>
          <h3 style={{ margin: "0 0 22px", fontSize: 16, fontWeight: 700, color: T.text }}>
            Account Information
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {fields.map(f => (
              <div key={f.label}>
                <label style={{ display: "block", fontSize: 14, color: T.muted, marginBottom: 6, fontWeight: 500 }}>
                  {f.label}
                </label>
                <input
                  type={f.type}
                  defaultValue={f.value}
                  readOnly={f.readOnly}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    background: f.readOnly ? T.cardAlt : T.bg,
                    border: `1px solid ${T.border}`,
                    borderRadius: 8, padding: "10px 14px",
                    color: f.readOnly ? T.faint : T.text,
                    fontSize: 13, outline: "none",
                    cursor: f.readOnly ? "not-allowed" : "text",
                  }}
                />
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
            <button style={{
              background: "transparent", border: `1px solid ${T.border}`,
              color: T.muted, borderRadius: 10, padding: "10px 22px",
              cursor: "pointer", fontSize: 13,
            }}>Cancel</button>
            <button style={{
              background: "linear-gradient(135deg, #2185ff, #1d66cc)",
              border: "none", color: "#fff", borderRadius: 10,
              padding: "10px 24px", cursor: "pointer", fontSize: 13, fontWeight: 700,
              boxShadow: "0 4px 14px rgba(33, 133, 255, 0.35)",
            }}>Save Changes</button>
          </div>
        </div>
      </div>

      {/* Password & danger zone */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 24 }}>
          <h3 style={{ margin: "0 0 18px", fontSize: 15, fontWeight: 700, color: T.text }}>
            Change Password
          </h3>
          {["Current Password", "New Password", "Confirm Password"].map(label => (
            <div key={label} style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 12, color: T.muted, marginBottom: 6 }}>{label}</label>
              <input type="password" placeholder="••••••••" style={{
                width: "100%", boxSizing: "border-box",
                background: T.bg, border: `1px solid ${T.border}`,
                borderRadius: 8, padding: "10px 14px",
                color: T.text, fontSize: 13, outline: "none",
              }} />
            </div>
          ))}
          <button style={{
            background: "linear-gradient(135deg, #2185ff, #1d66cc)",
            border: "none", color: "#fff", borderRadius: 10,
            padding: "10px 22px", cursor: "pointer", fontSize: 13, fontWeight: 700,
          }}>Update Password</button>
        </div>

        <div style={{ background: T.card, border: `1px solid rgba(239,68,68,0.2)`, borderRadius: 14, padding: 24 }}>
          <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700, color: T.red }}>
            Danger Zone
          </h3>
          <p style={{ margin: "0 0 20px", fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
            Actions below are permanent and cannot be undone.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button style={{
              background: "transparent", border: `1px solid ${T.border}`,
              color: T.orange, borderRadius: 10, padding: "10px 16px",
              cursor: "pointer", fontSize: 13, fontWeight: 600, textAlign: "left",
            }}>⚠️ Deactivate Account</button>
            <button style={{
              background: "rgba(239,68,68,0.08)", border: `1px solid rgba(239,68,68,0.25)`,
              color: T.red, borderRadius: 10, padding: "10px 16px",
              cursor: "pointer", fontSize: 13, fontWeight: 600, textAlign: "left",
            }}>🗑 Delete Account Permanently</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── USER DASHBOARD SHELL ─────────────────────────────────────────────────────
export default function UserDashboard() {
  const [active, setActive] = useState("overview");

  const pages = {
    overview: <OverviewPage />,
    orders:   <MyOrdersPage />,
    mykit:    <MyKitPage />,
    designs:  <SavedDesignsPage />,
    profile:  <ProfilePage />,
  };

  return (
    <div style={{
      display: "flex", height: "100vh",
      background: T.bg,
      fontFamily: "system-ui, -apple-system, sans-serif",
      overflow: "hidden",
    }}>
      {/* ── Sidebar ── */}
      <div style={{
        width: 210, background: T.sidebar, borderRight: `1px solid ${T.border}`,
        display: "flex", flexDirection: "column", padding: "24px 0", flexShrink: 0,
      }}>
        {/* Logo */}
        <div style={{ padding: "0 20px 24px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: T.accentDim, border: `1px solid ${T.accentBorder}`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{ color: T.accent, fontSize: 16, fontWeight: 700 }}>S</span>
            </div>
            <span style={{ color: T.text, fontWeight: 700, fontSize: 15 }}>SportsKit</span>
          </div>
        </div>

        {/* User mini profile */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "rgba(34,197,94,0.15)", border: `1.5px solid ${T.accentBorder}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: 700, color: T.accent, flexShrink: 0,
            }}>{USER.avatar}</div>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {USER.name}
              </p>
              <p style={{ margin: 0, fontSize: 11, color: T.accent }}>{USER.tier}</p>
            </div>
          </div>
        </div>

        {/* Nav links */}
        <nav style={{ padding: "16px 12px", flex: 1 }}>
          {NAV.map(n => (
            <div key={n.key}
              onClick={() => setActive(n.key)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 12px", borderRadius: 8, marginBottom: 2,
                cursor: "pointer",
                background: active === n.key ? T.accentDim : "transparent",
                border: active === n.key ? `1px solid ${T.accentBorder}` : "1px solid transparent",
                color: active === n.key ? T.accent : T.muted,
                fontWeight: active === n.key ? 600 : 400,
                fontSize: 13, transition: "all 0.15s",
              }}
            >
              <span style={{ fontSize: 15 }}>{n.icon}</span>
              {n.label}
            </div>
          ))}
        </nav>

        {/* Logout */}
        <div style={{ padding: "0 12px" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 12px", borderRadius: 8, cursor: "pointer",
            color: T.faint, fontSize: 13,
            border: "1px solid transparent",
          }}>
            <span style={{ fontSize: 15 }}>🚪</span> Log Out
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
        {pages[active] || <OverviewPage />}
      </div>
    </div>
  );
}